import {translate} from "./i18n.js";
import Translation from "../../common/db/types/Translation";

export default class CmsHelper {
    static getPageIcon(page) {
        if (page.meta?.socialImage) {
            return page.meta?.socialImage;
        }

        const pageDetails = CmsHelper.getPageDetails(page);
        return pageDetails ? pageDetails.icon : `window`
    }

    static getPageDetails(page) {
        if (page.homePage === true) {
            return {
                value: `home`,
                description: translate(`Home Page|Page d'accueil`),
                variant: `primary`,
                icon: getPublicIcon(`window-home`),
                glyph: `fa-house`
            };
        }
        switch (page.type) {
            case `page`:
            case `blocks`:
                return {
                    value: `blocks`,
                    description: translate(`Editable|Éditable`),
                    variant: `light`,
                    icon: getPublicIcon(`window-editable`),
                    glyph: `fa-pen`
                };
            case `map`:
                return {
                    value: `map`,
                    description: translate(`Map|Plan`),
                    variant: `light`,
                    icon: getPublicIcon(`window-map`),
                    glyph: `fa-map-location-dot`
                };
            case `cms-item-view`:
                return {
                    value: `cms-item-view`,
                    description: translate(`Item Details|Détails d'un élément`),
                    variant: `light`,
                    icon: getPublicIcon(`window-details`),
                    glyph: `fa-memo-list`
                };
            case `cms-item-list`:
                return {
                    value: `cms-item-list`,
                    description: translate(`Item List|Liste d'éléments`),
                    variant: `light`,
                    icon: getPublicIcon(`window-list`),
                    glyph: `fa-square-list`
                };
            default:
                return {
                    value: `template`,
                    description: translate(`Template|Gabarit`),
                    variant: `warning`,
                    icon: getPublicIcon(`website`),
                    glyph: `fa-lock`
                };
        }
    }

    static getPageStatus(status) {
        switch (status) {
            case `archive`:
                return {
                    value: `archive`,
                    label: translate(`Archived|Archivée`),
                    variant: `danger`,
                    glyph: `fa-archive`
                };
            case `pending`:
                return {
                    value: `pending`,
                    label: translate(`Pending Review|En attente`),
                    variant: `light-warning`,
                    glyph: `fa-clock`
                };
            case `published`:
                return {
                    value: `published`,
                    label: translate(`Published|Publiée`),
                    variant: `light-success`,
                    glyph: `fa-circle-check`
                };
            default:
                return {
                    value: `draft`,
                    label: translate(`Draft|Brouillon`),
                    variant: `light`,
                    glyph: `fa-file-pen`
                };
        }
    }

    static textToPermalink(text, separator = `-`) {
        text = text.toString().toLowerCase().trim();

        const sets = [
            {to: `a`, from: `[ÀÁÂÃÅÆĀĂĄẠẢẤẦẨẪẬẮẰẲẴẶ]`},
            {to: `ae`, from: `[Ä]`},
            {to: `c`, from: `[ÇĆĈČ]`},
            {to: `d`, from: `[ÐĎĐÞ]`},
            {to: `e`, from: `[ÈÉÊËĒĔĖĘĚẸẺẼẾỀỂỄỆ]`},
            {to: `g`, from: `[ĜĞĢǴ]`},
            {to: `h`, from: `[ĤḦ]`},
            {to: `i`, from: `[ÌÍÎÏĨĪĮİỈỊ]`},
            {to: `j`, from: `[Ĵ]`},
            {to: `ij`, from: `[Ĳ]`},
            {to: `k`, from: `[Ķ]`},
            {to: `l`, from: `[ĹĻĽŁ]`},
            {to: `m`, from: `[Ḿ]`},
            {to: `n`, from: `[ÑŃŅŇ]`},
            {to: `o`, from: `[ÒÓÔÕØŌŎŐỌỎỐỒỔỖỘỚỜỞỠỢǪǬƠ]`},
            {to: `oe`, from: `[ŒÖ]`},
            {to: `p`, from: `[ṕ]`},
            {to: `r`, from: `[ŔŖŘ]`},
            {to: `s`, from: `[ŚŜŞŠ]`},
            {to: `ss`, from: `[ß]`},
            {to: `t`, from: `[ŢŤ]`},
            {to: `u`, from: `[ÙÚÛŨŪŬŮŰŲỤỦỨỪỬỮỰƯ]`},
            {to: `ue`, from: `[Ü]`},
            {to: `w`, from: `[ẂŴẀẄ]`},
            {to: `x`, from: `[ẍ]`},
            {to: `y`, from: `[ÝŶŸỲỴỶỸ]`},
            {to: `z`, from: `[ŹŻŽ]`},
            {to: `-`, from: `[·/_,:;']`}
        ];

        sets.forEach((set) => {
            text = text.replace(new RegExp(set.from, `gi`), set.to);
        })

        text = text
            .toString()
            .toLowerCase()
            .replace(/\s+/g, `-`) // Replace spaces with -
            .replace(/&/g, `-and-`) // Replace & with 'and'
            .replace(/[^\w-]+/g, ``) // Remove all non-word chars
            .replace(/--+/g, `-`) // Replace multiple - with single -
            .replace(/^-+/, ``) // Trim - from start of text
            .replace(/-+$/, ``); // Trim - from end of text

        if (typeof separator !== `undefined` && separator !== `-`) {
            text = text.replace(/-/g, separator);
        }

        return text;
    }

    static ensureTranslation(item, fieldName) {
        const val = item[fieldName];

        if (val instanceof Translation) {
            return item;
        }

        if (typeof val === `string`) {
            item[fieldName] = new Translation({ en: val });
            return item;
        }

        if (val && typeof val === `object`) {
            item[fieldName] = new Translation(val);
            return item;
        }

        // Otherwise, fallback to an empty new Translation
        item[fieldName] = new Translation();
        return item;
    }

    static getMediumName(medium) {
        switch (medium) {
            case `default`:
                return translate(`URL`);
            case `player`:
                return translate(`Kiosks|Bornes`);
            case `qr-code`:
                return translate(`QR Codes|Codes QR`);
            case `iframe`:
                return translate(`Iframe`);
            case `kiosk-to-phone`:
                return translate(`Kiosk to Phone|Borne vers tél.`);
            case `sharing`:
                return translate(`Sharing|Partage`);
            case `social`:
                return translate(`Social Net.|Réseaux soc.`);
            case `search`:
                return translate(`Search Eng.|Moteur rech.`);
            case `rdv`:
                return translate(`RDV|RDV`);
            case `website`:
                return translate(`Website|Site internet`);
            default:
                return translate(`Others|Autres`);
        }
    }

    static parseUTM(medium, source, website, map) {
        let data = {
            medium: medium,
            source: source,
            label: CmsHelper.getMediumName(medium),
            icon: `website`,
            name: medium
        }

        if (source) {
            if (source.indexOf(`?`)) {
                source = source.split(`?`)[0];
            }
            data.name = source;
        } else {
            if (medium === `default`) {
                if (website && website.domain) {
                    data.name = website.domain;
                } else {
                    data.name = data.label;
                }
            }
        }

        let mapItem;
        switch (medium) {
            case `website`:
                data.icon = `website`;
                break;
            case `player`:
                if (map) {
                    mapItem = map.items.find(i => i.integrations?.signage.playerId === source);
                    if (mapItem) {
                        data.name = mapItem.name;
                    }
                }
                data.icon = `kiosk`;
                data.medium = `kiosk`;
                break;
            case `qr-code`:
                if (map) {
                    mapItem = map.items.find(i => i.qrCode === source);
                    if (mapItem) {
                        data.name = mapItem.name;
                    }
                }
                data.icon = `qr-code`;
                break;
            case `rdv`:
                // source.icon = `email-open`;
                data.icon = `auth-sms`;
                break;
            case `search`:
                data.icon = `google`;
                break;
            case `sharing`:
                data.icon = `sharing`;
                break;
            case `kiosk-to-phone`:
                switch (data.source) {
                    case `sms`:
                        data.icon = `send-to-phone`;
                        break;
                    case `qr-code`:
                        data.icon = `kiosk-qr-code`;
                        break;
                }
                break;
            case `social`:
                switch (data.source) {
                    case `facebook`:
                        data.icon = `brands/facebook`;
                        break;
                    case `linkedin`:
                        data.icon = `brands/linkedin`;
                        break;
                    case `twitter`:
                        data.icon = `brands/twitter`;
                        break;
                    case `youtube`:
                        data.icon = `brands/youtube`;
                        break;
                    default:
                        data.icon = `brands/facebook`;
                        break;
                }
                break;
            case `iframe`:
                data.icon = `window`;
                break;
            case `blog`:
                data.icon = `feed`;
                break;
        }
        return data;
    }
}
